import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  BallastType,
  InsertBallastTypeBody,
  UpdateBallastTypeNewImageBody,
  UpdateBallastTypeRemoveImageBody,
} from "../../../../../http/border/types/ballastTypes";
import { useBallastTypesApi } from "../../../../../http/border/useBallastTypes";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface BallastTypeFormBody {
  name: string;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface BallastTypeDetailsProps {
  ballastType?: BallastType;
  isDisabled?: boolean;
  isLoading?: boolean;
  tabbed?: boolean;
  onSubmitSuccess?: (ballastTypeId?: string) => void;
}

const BallastTypeForm: FC<BallastTypeDetailsProps> = ({
  ballastType,
  isDisabled,
  isLoading,
  onSubmitSuccess,
}: BallastTypeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ballastTypesApi = useBallastTypesApi();

  const {
    component,
    newImage,
    removeImage,
    onSave: onImageSave,
  } = useImageUploader({
    defaultImage: ballastType?.image ?? undefined,
    isLoading: !!isLoading,
    disabled: isDisabled || isSubmitting,
  });

  const defaultValues: BallastTypeFormBody = {
    name: ballastType?.name ?? "",
    nameIt: ballastType?.nameIt ?? null,
    nameDe: ballastType?.nameDe ?? null,
    nameEs: ballastType?.nameEs ?? null,
    nameFr: ballastType?.nameFr ?? null,
    order: ballastType?.order ?? 50,
    visibility: ballastType?.visibility ?? "hidden",
  };

  const onSubmit = () => async (formData: BallastTypeFormBody) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name.trim(),
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
    };

    if (ballastType) {
      if (newImage) {
        try {
          const signedUrl = await ballastTypesApi.generateUploadImageUrl(ballastType.id, newImage.name, newImage.type);
          const res = await api.uploadFile(signedUrl, newImage);
          if (!res.ok) {
            dispatch(showErrorNotification(i18n, res));
            setIsSubmitting(false);
            return;
          }
          const body: UpdateBallastTypeNewImageBody = {
            ...commonBody,
            image: newImage.name,
          };
          await ballastTypesApi.updateAdminBallastType(ballastType.id, body);
          onImageSave();
          onSubmitSuccess?.();
          dispatch(showSuccessNotification(i18n.translation));
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        try {
          const body: UpdateBallastTypeRemoveImageBody = {
            ...commonBody,
            removeImage: removeImage,
          };
          await ballastTypesApi.updateAdminBallastType(ballastType.id, body);
          onImageSave();
          onSubmitSuccess?.();
          dispatch(showSuccessNotification(i18n.translation));
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      }
    } else {
      try {
        const body: InsertBallastTypeBody = {
          ...commonBody,
          image: newImage?.name,
        };

        const res = await ballastTypesApi.insertAdminBallastType(body);
        if (newImage) {
          const signedUrl = await ballastTypesApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            setIsSubmitting(false);
            return;
          }
          onImageSave();
        }
        onSubmitSuccess?.(res.id);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<BallastTypeFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isLoading || isDisabled}
      onSubmit={onSubmit}
    >
      {({ register, reset, formState: { errors }, control, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [ballastType]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading || isSubmitting}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting || isLoading || isDisabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value}
                      isInvalid={isInvalid}
                      disabled={isSubmitting || isLoading || isDisabled}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => {
                        field.onChange(value ?? 50);
                        field.onBlur();
                      }}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default BallastTypeForm;
